/**
 * The shapes a text item's glyphs can follow. Every value but NONE lays the text out along a
 * path: one generated from the sliders (see `text-shape-path.library.ts`), or for PATH the one
 * the user drew on the canvas; NONE is the plain wrapped box.
 */
export enum TextShapeType {
  NONE = 'none',
  CURVE = 'curve',
  CIRCLE = 'circle',
  WAVE = 'wave',
  SPIRAL = 'spiral',
  HEART = 'heart',
  SQUARE = 'square',
  PATH = 'path',
}

/**
 * Serialized shape of a text item. `amount` is the one slider every shaped type but SPIRAL
 * exposes, in that type's own units: the bend of a CURVE (−100..100, negative bends down), the
 * part of a CIRCLE / HEART / SQUARE perimeter the text spans (1..100 %), the strength of a WAVE
 * (−100..100: 0 is flat, positive starts with a peak, negative with a trough). `count` is the
 * number of WAVE periods (1..3); the other types ignore it.
 */
export interface TextShapeObject {
  type: TextShapeType;
  amount: number;
  count: number;
  /**
   * The SVG path a PATH shape follows, in the item's own units with its bounding box at the
   * origin — the freeform stroke the user drew, stored as it was drawn; the item's scale turns it
   * with the text. `null` for every other type, which generate their path from the sliders.
   */
  path: string | null;
}

/** How a shape's path shows itself under the text while the item is selected. */
export enum TextShapeGuideStyle {
  NONE = 'none',
  /** The whole path as a thin solid line, as the design draws it under a circle, square, wave or spiral. */
  SOLID = 'solid',
  /** A dashed line, only while the text does not yet fill the path (a drawn path). */
  DASHED_WHILE_UNFILLED = 'dashedWhileUnfilled',
}

export const TEXT_SHAPE_CURVE_AMOUNT_MIN = -100;
export const TEXT_SHAPE_CURVE_AMOUNT_MAX = 100;
/**
 * A figure's text covers at least a quarter of its outline: the figure fills its box, empty part
 * included, and at 7 the ring was already 14 times the text, wider than the page (owner, 2026-10-29;
 * the Figma's own samples are 100, 50 and 25).
 */
export const TEXT_SHAPE_COVERAGE_MIN = 25;
export const TEXT_SHAPE_COVERAGE_MAX = 100;
export const TEXT_SHAPE_WAVE_AMOUNT_MIN = -100;
export const TEXT_SHAPE_WAVE_AMOUNT_MAX = 100;
export const TEXT_SHAPE_WAVE_COUNT_MIN = 1;
export const TEXT_SHAPE_WAVE_COUNT_MAX = 3;

export const DEFAULT_TEXT_SHAPE_COUNT = 1;

/** The placeholder a freshly drawn path carries until the user replaces it: the same "add your text" a plain new text item shows. */
export const TEXT_ON_PATH_PLACEHOLDER_TEXT_KEY = 'pmwjs_add_text';

/** The slider value a freshly picked shape starts at, so the pick is visible at once. */
export const DEFAULT_TEXT_SHAPE_AMOUNT: Record<TextShapeType, number> = {
  [TextShapeType.NONE]: 0,
  [TextShapeType.CURVE]: 50,
  [TextShapeType.CIRCLE]: TEXT_SHAPE_COVERAGE_MAX,
  [TextShapeType.WAVE]: 50,
  [TextShapeType.SPIRAL]: 0,
  [TextShapeType.HEART]: TEXT_SHAPE_COVERAGE_MAX,
  [TextShapeType.SQUARE]: TEXT_SHAPE_COVERAGE_MAX,
  [TextShapeType.PATH]: 0,
};

export const DEFAULT_TEXT_SHAPE_OBJECT: TextShapeObject = {
  type: TextShapeType.NONE,
  amount: DEFAULT_TEXT_SHAPE_AMOUNT[TextShapeType.NONE],
  count: DEFAULT_TEXT_SHAPE_COUNT,
  path: null,
};
